import { LegacyMvfPoint } from "algorithm/mvfs";
import * as d3 from "d3";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { D3Axis, SvgTooltipContext } from "./MVFGraph";

export type DragHandler = (
  event: d3.D3DragEvent<Element, unknown, null>,
  selection: d3.Selection<Element, unknown, null, undefined>,
) => void;

export function DraggableCircle({
  dragHandler,
  point,
  title,
  color,
  doubleClickHandler,
  xAxis,
  yAxis,
}: {
  dragHandler: DragHandler;
  point: LegacyMvfPoint;
  title?: string;
  color: string;
  doubleClickHandler?: (event: MouseEvent) => void;
  xAxis: D3Axis;
  yAxis: D3Axis;
}) {
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [isMouseOver, setIsMouseOver] = useState(false);
  const tooltipRef = useRef<SVGTextElement>(null);
  const circleRef = useRef<SVGCircleElement>(null);
  const [textDimensions, setTextDimensions] = useState({
    width: 0,
    height: 0,
  });

  const x = xAxis(point.dollar);
  const y = yAxis(point.value);

  const radius = 2;

  const attachEventListeners = useCallback(
    (element: Element) => {
      const selection = d3.select(element);
      d3
        .drag()
        .on("start", () => setIsMouseDown(true))
        .on("drag", (event) => dragHandler(event, selection))
        .on("end", () => setIsMouseDown(false))(selection);

      if (doubleClickHandler) {
        selection.on("dblclick", (e) => doubleClickHandler(e));
      }
      selection.on("mouseenter", () => setIsMouseOver(true));
      selection.on("mouseleave", () => setIsMouseOver(false));

      return () => {
        d3.drag().on("start", null).on("drag", null).on("end", null);
        selection.on("dblclick", null);
        selection.on("mouseenter", () => null);
        selection.on("mouseleave", () => null);
      };
    },
    [dragHandler, doubleClickHandler],
  );

  useEffect(() => {
    if (circleRef.current) {
      const cleanup = attachEventListeners(circleRef.current);
      return cleanup;
    }
  }, [attachEventListeners]);

  const showTooltip = isMouseDown || isMouseOver;

  useEffect(() => {
    if (tooltipRef.current) {
      const bbox = tooltipRef.current.getBBox();
      setTextDimensions({ width: bbox.width, height: bbox.height });
    }
  }, [title, showTooltip]);

  const svgTooltipContext = useContext(SvgTooltipContext);
  if (point.dollar < xAxis.domain()[0] || point.dollar > xAxis.domain()[1]) {
    return null;
  }

  return (
    <>
      <circle
        ref={circleRef}
        style={{ cursor: "pointer" }}
        fill={color}
        r={radius}
        cx={x}
        cy={y}
        stroke={color}
        strokeWidth={3}
      />
      {showTooltip &&
        title &&
        svgTooltipContext?.container &&
        createPortal(
          <g>
            <rect
              x={x - textDimensions.width / 2 - 5}
              y={y - textDimensions.height - 13}
              width={textDimensions.width + 10}
              height={textDimensions.height + 4}
              className="light:fill-[#f5f5f4] dark:fill-[#181818]"
              rx={2}
            />
            <text
              ref={tooltipRef}
              x={x}
              y={y - 15}
              textAnchor="middle"
              className="dark:fill-white light:fill-black"
              fontSize="12px"
            >
              {title}
            </text>
          </g>,
          svgTooltipContext.container,
        )}
    </>
  );
}
